/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import tenantUserServices from '../../../services/clientAdmin/tenantUserServices';
import { getDepartments, getTeams } from '../../../services/clientAdmin/departmentServices';

export default function EditLearnerModal({ learner, onClose, onSaved }) {
  const [form, setForm] = useState({
    phone:              learner.phone              || '',
    departmentId:       learner.departmentId       || '',
    teamId:             learner.teamId             || '',
    employeeRollNumber: learner.employeeRollNumber || '',
    notes:              learner.notes              || '',
  });

  const [departments,  setDepartments]  = useState([]);
  const [teams,        setTeams]        = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  useEffect(() => {
    getDepartments()
      .then(r => {
        if (r.ok) {
          const data = r.data?.departments || r.data || [];
          setDepartments(data.filter(d => d.isActive));
        }
      })
      .finally(() => setLoadingDepts(false));
  }, []);

  useEffect(() => {
    if (!form.departmentId) { setTeams([]); return; }
    setLoadingTeams(true);
    getTeams(form.departmentId)
      .then(r => {
        if (r.ok) {
          const data = r.data?.teams || r.data || [];
          setTeams(data.filter(t => t.isActive));
        }
      })
      .finally(() => setLoadingTeams(false));
  }, [form.departmentId]);

  function handleChange(field, value) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'departmentId') next.teamId = '';
      return next;
    });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
      if (form.departmentId && !form.teamId) {
      setError('Please select a team for the chosen department.');
      setSaving(false);
      return;
    }
    try {
      const payload = {
        phone:              form.phone              || null,
        teamId:             form.teamId ? Number(form.teamId) : null,
        employeeRollNumber: form.employeeRollNumber || null,
        notes:              form.notes              || null,
      };
      const res = await tenantUserServices.updateTenantUser(learner.id, payload);
      if (res.ok) {
        onSaved();
      } else {
        setError(res.data?.message || 'Failed to update learner. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const name = `${learner.firstName || ''} ${learner.lastName || ''}`.trim();

 return createPortal(
<div className="fixed inset-x-0 bottom-0 top-[65px] z-50 bg-black/40 
                flex items-center justify-center px-6 py-6">
<div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col" 
     style={{ maxHeight: 'calc(100vh - 65px - 48px)' }}>
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Learner</h2>
            <p className="text-xs text-gray-400 mt-0.5">{name}</p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Fixed info */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
            <p><span className="text-gray-400">Name:</span>{' '}
              <span className="font-medium text-gray-800">{name}</span></p>
            <p><span className="text-gray-400">Email:</span>{' '}
              <span className="font-medium text-gray-800 truncate block max-w-full">{learner.email}</span></p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
            <input
              type="text"
              value={form.phone}
              onChange={e => handleChange('phone', e.target.value)}
              placeholder="+91 XXXXX XXXXX"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>

          {/* Department + Team */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              {loadingDepts ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : (
                <select
                  value={form.departmentId}
                  onChange={e => handleChange('departmentId', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                >
                  <option value="">No Department</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Team</label>
              {loadingTeams ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : (
                <select
                  value={form.teamId}
                  onChange={e => handleChange('teamId', e.target.value)}
                  disabled={!form.departmentId}
                  className="w-full border border-gray-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <option value="">No Team</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
              {!form.departmentId && (
                <p className="text-xs text-gray-300 mt-1">Select a department first</p>
              )}
            </div>
          </div>

          {/* Roll Number */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Roll Number</label>
            <input
              type="text"
              value={form.employeeRollNumber}
              onChange={e => handleChange('employeeRollNumber', e.target.value)}
              placeholder="e.g. EMP-001"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Any additional notes about this learner…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end px-6 py-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  , document.body);
}